import React, { useEffect, useRef } from 'react';
import webrtcAdapter from 'webrtc-adapter';
import { useVideoInputField } from 'hooks/useVideoInputField';
import VideoRenderer from 'components/VideoRenderer';
import { type Control, type UseFormSetValue } from 'react-hook-form';
import { TabPanelInner } from 'components/Tabs';
import DeviceSelect from 'components/DeviceSelect';
import { useTranslation } from 'react-i18next';
import { usePublishingFeed } from 'hooks/usePublishingFeed';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { RoomSettingsFormValues } from 'features/room/settings/RoomSettings';
import {
  hdStreamToggled,
  mirrorVideoToggled,
  preserveAspectRatioToggled,
  selectHDVideoSettings,
  selectLocalVideoBroadcasting,
  selectLocalVideoEnabled,
  selectMirrorVideo,
  selectPreserveAspectRatio,
} from 'features/streaming/streamingSlice';
import { useTemporaryMedia } from 'hooks/useTemporaryMedia';
import {
  disableDeviceAutoChangeAlert,
  selectDeviceAutoChangeStatus,
} from 'features/user-media/userMediaSlice';
import { Box } from '@mui/material';
import { streamingDeviceChanged } from 'features/streaming/actions';
import { logger } from 'utils/logger';
import { useMediaDevices } from 'hooks/useMediaDevices';
import AudioOnlySetting from 'features/room/settings/tabs/AudioOnlySetting';
import SettingControl from 'components/SettingControl';
import { Switch } from 'components/Switch';

export type VideoTabProps = {
  control: Control<RoomSettingsFormValues>;
  setValue: UseFormSetValue<RoomSettingsFormValues>;
};

const VideoTab = ({ control, setValue }: VideoTabProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('settings');

  const isVideoBroadcasting = useAppSelector(selectLocalVideoBroadcasting);
  const isVideoEnabled = useAppSelector(selectLocalVideoEnabled);
  const deviceAutoChange = useAppSelector(selectDeviceAutoChangeStatus);

  const hdVideoSettings = useAppSelector(selectHDVideoSettings);

  const preserveAspectRatio = useAppSelector(selectPreserveAspectRatio);
  const mirrorVideo = useAppSelector(selectMirrorVideo);

  const { camPermissions, activeMediaDevices, persistDevices } = useMediaDevices();

  const { videoInputOptions, videoInputPlaceholder } = useVideoInputField();
  const { videoStream } = usePublishingFeed('media');

  const {
    loading: videoLoading,
    requestTemporaryMedia,
    mediaStream: tempVideoStream,
    cleanupTemporaryMedia,
  } = useTemporaryMedia();

  const deviceContextRef = useRef({
    activeMediaDevices,
    videoStream,
    isVideoBroadcasting,
  });

  deviceContextRef.current = {
    activeMediaDevices,
    videoStream,
    isVideoBroadcasting,
  };

  useEffect(() => {
    const deviceContext = deviceContextRef.current;
    if (!deviceContext.videoStream && deviceContext.activeMediaDevices.videoinput) {
      requestTemporaryMedia({
        video: {
          deviceId: {
            exact: deviceContext.activeMediaDevices.videoinput,
          },
        },
      });
    }

    return () => {
      cleanupTemporaryMedia();
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    const requestVideo = async () => {
      if (activeMediaDevices.videoinput) {
        await requestTemporaryMedia({
          video: {
            deviceId: {
              exact: activeMediaDevices.videoinput,
            },
          },
        });
      }
    };

    if (deviceAutoChange.videoinput) {
      setValue('videoinput', activeMediaDevices.videoinput || '', { shouldDirty: true });
      dispatch(disableDeviceAutoChangeAlert('videoinput'));

      if (!isVideoEnabled) {
        requestVideo();
      }
    }
  }, [
    activeMediaDevices.videoinput,
    isVideoEnabled,
    deviceAutoChange.videoinput,
    requestTemporaryMedia,
    setValue,
    dispatch,
  ]);

  const handleCameraChange = async (deviceId: string) => {
    cleanupTemporaryMedia();

    if (!deviceId) {
      return;
    }

    persistDevices(deviceId, 'videoinput');

    if (!isVideoBroadcasting) {
      await requestTemporaryMedia({
        video: {
          deviceId: {
            exact: deviceId,
          },
        },
      });
    }

    logger
      .remote({ action: true })
      .info(
        `User changed videoinput media device from ${activeMediaDevices.videoinput} to ${deviceId}`
      );

    dispatch(
      streamingDeviceChanged({
        id: deviceId,
        kind: 'videoinput',
      })
    );
  };

  const handleHDStreamChange = () => {
    dispatch(hdStreamToggled());
  };

  const handlePreserveAspectRatioChange = () => {
    dispatch(preserveAspectRatioToggled());
  };

  const handleMirrorVideoChange = () => {
    dispatch(mirrorVideoToggled());
  };

  return (
    <TabPanelInner>
      <div>
        <VideoRenderer
          mediaStream={videoStream || tempVideoStream}
          sx={{ mb: 8 }}
          loading={videoLoading}
        />
        <DeviceSelect
          id="settings-video-select"
          name="videoinput"
          control={control}
          icon="camera"
          onChange={handleCameraChange}
          options={videoInputOptions}
          disabled={camPermissions !== 'granted'}
          placeholder={videoInputPlaceholder}
          ariaLabel={t('device_dropdowns.videoinput.aria_label')}
        />
      </div>
      <Box sx={{ mt: 8 }}>
        <SettingControl
          label={t('video.preserve_aspect_ratio')}
          alignItems="flex-start"
          control={
            <Switch
              onChange={handlePreserveAspectRatioChange}
              checked={preserveAspectRatio}
              slotProps={{
                input: {
                  'aria-label': preserveAspectRatio
                    ? t('video.preserve_aspec_ratio_disable_aria_label')
                    : t('video.preserve_aspec_ratio_enable_aria_label'),
                },
              }}
            />
          }
        />
      </Box>
      <Box sx={{ mt: 8 }}>
        <SettingControl
          label={t('video.mirror_video')}
          helperText={t('video.mirror_video_help')}
          alignItems="flex-start"
          control={
            <Switch
              onChange={handleMirrorVideoChange}
              checked={mirrorVideo}
              slotProps={{
                input: {
                  'aria-label': mirrorVideo
                    ? t('video.mirror_video_disable_aria_label')
                    : t('video.mirror_video_enable_aria_label'),
                },
              }}
            />
          }
        />
      </Box>

      {webrtcAdapter.browserDetails.browser !== 'firefox' ? (
        <Box sx={{ mt: 8 }}>
          <SettingControl
            label={t('video.hd_stream_label')}
            helperText={t('video.hd_stream_warning')}
            alignItems="flex-start"
            control={
              <Switch
                onChange={handleHDStreamChange}
                checked={hdVideoSettings.enabled}
                slotProps={{
                  input: {
                    'aria-label': hdVideoSettings.enabled
                      ? t('video.hd_stream_disable_aria_label')
                      : t('video.hd_stream_enable_aria_label'),
                  },
                }}
              />
            }
          />
        </Box>
      ) : null}

      <AudioOnlySetting />
    </TabPanelInner>
  );
};

export default VideoTab;
